.skipLink {
  position: absolute;
  inset-inline-start: var(--space-4);
  inset-block-start: -10rem;
  z-index: 200;
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  box-shadow: var(--shadow-lg);
  font-weight: var(--weight-semibold);
}

.skipLink:focus {
  inset-block-start: var(--space-2);
}

.topbar {
  position: sticky;
  inset-block-start: 0;
  z-index: 50;
  background: var(--color-topbar-bg);
  color: var(--color-topbar-text);
  /* a thin sand rule under the bar: the "ledger line" of the identity */
  box-shadow: inset 0 -2px 0 color-mix(in srgb, var(--color-topbar-indicator) 35%, transparent);
}

.topbarRow {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-block-size: var(--topbar-height);
  max-inline-size: var(--content-max);
  margin-inline: auto;
  padding-inline: var(--gutter);
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: inherit;
  text-decoration: none;
  font-weight: var(--weight-bold);
  font-size: var(--text-lg);
  letter-spacing: 0.01em;
  flex: none;
}

.brandName {
  white-space: nowrap;
}

.orgPill {
  display: inline-flex;
  flex: 0 1 auto;
  align-items: center;
  gap: var(--space-2);
  min-inline-size: 0;
  max-inline-size: 18rem;
  min-block-size: 2.25rem;
  padding-inline: var(--space-3) var(--space-2);
  border: var(--border-width) solid rgb(255 255 255 / 0.18);
  border-radius: var(--radius-pill);
  background: rgb(255 255 255 / 0.06);
  color: inherit;
  cursor: pointer;
}

.orgPill[data-hovered] {
  background: var(--color-topbar-hover);
}

.orgName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: var(--weight-semibold);
  font-size: var(--text-sm);
}

.envTag {
  flex: none;
  padding: 0 var(--space-2);
  border-radius: var(--radius-pill);
  /* Test environments use the "lab" violet family (distinct from any orange/amber chip). */
  background: var(--color-lab-on-dark-bg);
  color: var(--color-lab-on-dark-text);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  white-space: nowrap;
}

.envTag[data-env='production'] {
  background: rgb(255 255 255 / 0.9);
  color: var(--nile-900);
}

.nav {
  display: flex;
  /* Never narrower than its links: a shrunken nav let them paint under the search button. */
  flex: none;
  align-items: stretch;
  align-self: stretch;
  gap: var(--space-1);
  margin-inline-start: var(--space-2);
}

.navLink {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding-inline: var(--space-3);
  color: var(--color-topbar-muted);
  text-decoration: none;
  font-weight: var(--weight-medium);
  font-size: var(--text-md);
  white-space: nowrap;
  border-radius: var(--radius-sm);
}

.hubIcon {
  display: inline-flex;
}

.hubLabel {
  white-space: nowrap;
}

.navLink:hover {
  color: var(--color-topbar-text);
  background: var(--color-topbar-hover);
}

.navLink[aria-current='page'] {
  color: var(--color-topbar-text);
  font-weight: var(--weight-semibold);
}

.navLink[aria-current='page']::after {
  content: '';
  position: absolute;
  inset-inline: var(--space-3);
  inset-block-end: 0;
  block-size: 3px;
  border-radius: 3px 3px 0 0;
  background: var(--color-topbar-indicator);
}

.spacer {
  flex: 1;
}

.toolButton {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  min-block-size: 2.25rem;
  padding-inline: var(--space-3);
  border: var(--border-width) solid transparent;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-topbar-text);
  font-size: var(--text-sm);
  cursor: pointer;
  white-space: nowrap;
}

.toolButton[data-hovered],
.toolButton:hover {
  background: var(--color-topbar-hover);
}

.searchButton {
  /* The search gives way first when the bar is tight; its label is cut, never the hub links. */
  flex: 0 1 15rem;
  min-inline-size: 2.5rem;
  overflow: hidden;
  justify-content: flex-start;
  border-color: rgb(255 255 255 / 0.16);
  background: rgb(255 255 255 / 0.05);
  color: var(--color-topbar-muted);
}

.searchLabel {
  min-inline-size: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kbd {
  flex: none;
  margin-inline-start: auto;
  padding: 0 var(--space-1);
  border: var(--border-width) solid rgb(255 255 255 / 0.2);
  border-radius: var(--radius-sm);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  direction: ltr;
}

.avatar {
  display: grid;
  place-items: center;
  inline-size: 2rem;
  block-size: 2rem;
  border-radius: 50%;
  background: var(--sand-300);
  color: var(--nile-950);
  font-weight: var(--weight-bold);
  font-size: var(--text-sm);
}

.navStrip {
  display: none;
}

.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-inline-size: 0;
  outline: none;
}

/* Menus (org switcher, account) */
.menuPopover {
  min-inline-size: 16rem;
  max-inline-size: min(22rem, calc(100vw - 2 * var(--space-4)));
  max-block-size: 70dvh;
  overflow: auto;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  box-shadow: var(--shadow-lg);
  outline: none;
}

.menu {
  padding: var(--space-1);
  outline: none;
}

.menuHeader {
  padding: var(--space-2) var(--space-3) var(--space-1);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  letter-spacing: 0.03em;
}

.menuItem {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  cursor: pointer;
  outline: none;
}

.menuItem[data-focused] {
  background: var(--color-primary-subtle);
  color: var(--color-primary-subtle-text);
}

.menuItemText {
  display: flex;
  flex-direction: column;
  min-inline-size: 0;
}

.menuItemTitle {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menuItemMeta {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.menuCheck {
  margin-inline-start: auto;
  color: var(--color-primary);
}

.menuSeparator {
  block-size: 1px;
  margin: var(--space-1) 0;
  background: var(--color-rule);
}

.identity {
  display: flex;
  flex-direction: column;
  padding: var(--space-2) var(--space-3);
  border-block-end: var(--border-width) solid var(--color-rule);
  margin-block-end: var(--space-1);
}

.identityName {
  font-weight: var(--weight-semibold);
}

.identityEmail {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  direction: ltr;
  unicode-bidi: isolate;
  text-align: start;
}

/* Command palette */
.paletteOverlay {
  position: fixed;
  inset: 0;
  z-index: 120;
  display: grid;
  place-items: start center;
  padding: 12vh var(--space-4) var(--space-4);
  background: var(--overlay);
}

.paletteModal {
  inline-size: min(38rem, 100%);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  outline: none;
}

.paletteDialog {
  outline: none;
}

.paletteSearch {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-block-end: var(--border-width) solid var(--color-rule);
}

.paletteSearch input {
  flex: 1;
  min-inline-size: 0;
  border: 0;
  background: transparent;
  font-size: var(--text-lg);
  outline: none;
}

.paletteSearch input::-webkit-search-cancel-button {
  display: none;
}

.paletteMenu {
  max-block-size: min(26rem, 60dvh);
  overflow: auto;
  padding: var(--space-2);
  outline: none;
}

.paletteSectionHeader {
  padding: var(--space-2) var(--space-3) var(--space-1);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
}

.paletteEmpty {
  padding: var(--space-6);
  text-align: center;
  color: var(--color-text-muted);
}

.paletteFooter {
  display: flex;
  gap: var(--space-4);
  padding: var(--space-2) var(--space-4);
  border-block-start: var(--border-width) solid var(--color-rule);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

/* Narrow screens: the module navigation moves to its own scrollable strip. */
/* Narrow desktops: the top bar drops its decorative hub icons (labels stay) so all six hubs fit. */
@media (max-width: 80rem) {
  .nav .hubIcon {
    display: none;
  }

  .nav .navLink {
    padding-inline: var(--space-2);
  }
}

@media (max-width: 64rem) {
  .nav {
    display: none;
  }

  .navStrip {
    display: flex;
    gap: var(--space-1);
    max-inline-size: var(--content-max);
    margin-inline: auto;
    padding-inline: var(--gutter);
    overflow-x: auto;
    scrollbar-width: none;
    min-block-size: 2.75rem;
  }

  .navStrip::-webkit-scrollbar {
    display: none;
  }

  .searchButton {
    min-inline-size: 0;
    padding-inline: var(--space-2);
  }

  .searchLabel,
  .kbd {
    display: none;
  }
}

@media (max-width: 40rem) {
  .brandName,
  .langLabel {
    display: none;
  }

  .orgPill {
    max-inline-size: 11rem;
  }

  /* The page itself shows the environment badge; the pill keeps the name readable. */
  .envTag {
    display: none;
  }

  .topbarRow {
    gap: var(--space-2);
  }

  /* Phones (IA rule 2): every hub is an icon with its label underneath, spread across the strip. */
  .navStrip {
    justify-content: space-around;
    min-block-size: 3.25rem;
  }

  .navStrip .navLink {
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    min-inline-size: 4.5rem;
    padding-block: var(--space-1);
    font-size: var(--text-xs);
  }

  .navStrip .navLink[aria-current='page']::after {
    inset-inline: 30%;
  }
}

/* Pages of the current hub (IA.md; council UX decision 2026-10-04) */
.pageStrip {
  display: flex;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-6, 24px);
  overflow-x: auto;
  border-block-start: 1px solid var(--color-border);
  background: var(--color-surface);
}

.pageLink {
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  color: var(--color-text);
  font-size: 13px;
  text-decoration: none;
  white-space: nowrap;
}

.pageLink:hover {
  background: var(--color-surface-sunken);
}

.pageLink[aria-current='page'] {
  border-color: var(--color-primary);
  color: var(--color-primary);
  font-weight: 600;
}

.pageLink:focus-visible {
  outline: 2px solid var(--color-focus);
  outline-offset: 2px;
}

.notBuiltTag {
  padding: 0 var(--space-2);
  border-radius: 999px;
  background: var(--sand-100, #f3ead9);
  color: var(--sand-700, #7a5a24);
  font-size: 11px;
  font-weight: 400;
}

/* Deployment-level test-environment strip (above the top bar; violet "lab" family). */
.testEnv {
  border-block-end: 1px solid var(--color-lab-border);
  background: var(--color-lab-bg);
  color: var(--color-lab-text);
}

.testEnvButton {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: var(--space-1) var(--space-4);
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.testEnvButton:focus-visible {
  outline: 2px solid var(--color-focus);
  outline-offset: -2px;
}

.testEnvDetail {
  margin: 0;
  padding: 0 var(--space-4) var(--space-2);
  font-size: 12px;
  text-align: center;
}

.testEnvInline {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin: 0 0 var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-lab-border);
  border-radius: var(--radius-md);
  background: var(--color-lab-bg);
  color: var(--color-lab-text);
  font-size: 13px;
}
